import { Link } from "react-router-dom";
import { ArrowRight, Leaf, ShieldCheck, Sparkles } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1486707471592-8e7eb7e36f78?auto=format&fit=crop&w=2200&q=80";
const STORY_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_jungle-retreat-3/artifacts/eviqbnza_why%20tarzan%20exists.jpg";
const ABOUT_VIDEO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_jungle-retreat-3/artifacts/e7fyb5j3_tarzan%20video.mp4";

export default function About() {
  return (
    <div className="bg-ivory">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[480px] w-full overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover animate-subtle-pan" />
        <div className="absolute inset-0 bg-gradient-to-b from-forest-900/40 to-forest-900/90" />
        <div className="relative container-tarzan h-full flex flex-col justify-end pb-20 text-ivory">
          <div className="eyebrow text-sand-200 mb-4">Our Story</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight max-w-3xl">A quiet rebellion against the ordinary holiday.</h1>
        </div>
      </section>

      {/* Story */}
      <section className="py-24">
        <div className="container-tarzan grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">Why Tarzan exists</div>
            <h2 className="font-serif text-4xl md:text-5xl text-ink leading-[1.05] tracking-tight">We don't list properties.<br/><em className="not-italic text-forest">We collect them.</em></h2>
            <div className="mt-6 text-ink-soft leading-relaxed space-y-5 text-base md:text-lg">
              <p>Tarzan began in a small coffee estate in Coorg in 2019, when our founders realised that India's most beautiful homes were locked away — known only to the families who built them, the friends they invited, and the neighbours who walked over for tea.</p>
              <p>We spend months on the road, drive thousands of kilometres of hill roads, and politely turn away nine homes for every one we add. Every Tarzan stay is a home we'd send our parents to.</p>
              <p>We believe luxury is silence. It is the right pillow, a wood-fired bath, and someone who remembers you take your coffee at six. We are building India's most thoughtful collection of nature retreats — slowly, and without compromise.</p>
            </div>
            <Link to="/stays" className="inline-flex items-center gap-2 mt-8 bg-forest text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-forest-700 transition-all">
              Explore the Collection <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden luxe-shadow"><img src={STORY_IMG} alt="" className="w-full h-full object-cover" /></div>
          </div>
        </div>
      </section>

      {/* Video section */}
      <section className="py-16 bg-forest-50/60">
        <div className="container-tarzan">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="eyebrow mb-3">In Motion</div>
            <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">A moment inside a Tarzan stay.</h2>
            <p className="text-ink-soft mt-4 leading-relaxed">Sixty seconds of what a morning, a meal and a walk feel like at our flagship.</p>
          </div>
          <div className="rounded-3xl overflow-hidden luxe-shadow aspect-video max-w-5xl mx-auto bg-forest-900">
            <video
              src={ABOUT_VIDEO}
              poster={STORY_IMG}
              controls
              muted
              playsInline
              className="w-full h-full object-cover"
              data-testid="about-video"
            />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-forest-50/60">
        <div className="container-tarzan">
          <div className="eyebrow mb-3">What we stand for</div>
          <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight max-w-2xl">Three promises we don't break.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-7">
            {[
              { i: ShieldCheck, h: "Vetted personally", b: "Every home is visited by a member of our team before it enters the collection. Photos lie. We don't." },
              { i: Leaf,        h: "Nature, always",    b: "If you can hear traffic from the verandah, it's not a Tarzan stay. We choose silence over scale." },
              { i: Sparkles,    h: "Quiet hospitality",  b: "A concierge who knows your name. A chef who learns your favourites. Help, when you want it; invisibility, when you don't." },
            ].map(({i:Ic,h,b}) => (
              <div key={h} className="bg-white rounded-3xl p-8 soft-shadow border border-forest/10">
                <div className="h-12 w-12 rounded-full bg-forest text-ivory grid place-items-center mb-5"><Ic className="h-5 w-5" strokeWidth={1.5} /></div>
                <div className="font-serif text-2xl text-ink">{h}</div>
                <p className="text-ink-soft mt-3 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
